import { Card, CardContent, Chip, Stack, Typography } from '@mui/joy';
import { ArrowRight } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import {
  AttributeValueChip,
  getAttributeValueLabel,
} from 'companySettings/components/PartFinderAttributeValues';
import { CompareGroup, CompareView } from 'companySettings/components/PartFinderVersionHistory';
import { AttributeType } from 'companySettings/enums';
import { useComparePartFinderProductTypeVersions } from 'companySettings/hooks';
import {
  IPartFinderAttributeValue,
  IPartFinderProductTypeAttributeChange,
  IPartFinderProductTypeAttributeChanges,
} from 'companySettings/types';
import { Color, Direction, Level, Variant } from 'theme/enums';

interface IAttributeChangeProps {
  change: IPartFinderProductTypeAttributeChange;
}

// A comparison may leave the attribute's type out; the values themselves tell it.
const inferValueType = (values: IPartFinderAttributeValue[]) => {
  if (values.some((value) => value?.rangeMin !== undefined || value?.rangeMax !== undefined)) {
    return AttributeType.NUMBER_RANGE;
  }

  return values.some((value) => value?.colorHex) ? AttributeType.COLOUR : AttributeType.VALUES;
};

// One attribute that changed between the versions: its list link and its values.
const AttributeChange = ({ change }: IAttributeChangeProps) => {
  const { t } = useTranslation();

  const { name, list, added, removed, changed, reordered } = change;
  const values = { added, removed, changed, reordered };
  const type =
    change.type ??
    inferValueType([
      ...(added ?? []),
      ...(removed ?? []),
      ...(changed ?? []).map((valueChange) => valueChange.to),
    ]);

  return (
    <Card variant={Variant.OUTLINED}>
      <CardContent>
        <Stack sx={{ gap: 1 }}>
          <Typography level={Level.TITLE_SM}>{name}</Typography>

          {list?.to && (
            <Typography level={Level.BODY_MD}>
              {t('labelNowUsesList', { name: list.to })}
            </Typography>
          )}

          {list && !list.to && list.from && (
            <Typography level={Level.BODY_MD}>
              {t('labelNoLongerUsesList', { name: list.from })}
            </Typography>
          )}

          {values?.added?.length > 0 && (
            <CompareGroup
              title={t('labelValuesAdded', { count: values.added.length })}
              color={Color.SUCCESS}
            >
              {values.added.map((value) => (
                <AttributeValueChip
                  key={getAttributeValueLabel(type, value, t)}
                  value={value}
                  type={type}
                />
              ))}
            </CompareGroup>
          )}

          {values?.removed?.length > 0 && (
            <CompareGroup
              title={t('labelValuesRemoved', { count: values.removed.length })}
              color={Color.DANGER}
            >
              {values.removed.map((value) => (
                <AttributeValueChip
                  key={getAttributeValueLabel(type, value, t)}
                  value={value}
                  type={type}
                />
              ))}
            </CompareGroup>
          )}

          {values?.changed?.length > 0 && (
            <CompareGroup
              title={t('labelValuesChanged', { count: values.changed.length })}
              color={Color.WARNING}
            >
              {values.changed.map(({ from, to }) => (
                <Stack
                  key={getAttributeValueLabel(type, to, t)}
                  direction={Direction.ROW}
                  sx={{ alignItems: 'center', gap: 0.5 }}
                >
                  <AttributeValueChip value={from} type={type} />
                  <ArrowRight />
                  <AttributeValueChip value={to} type={type} />
                </Stack>
              ))}
            </CompareGroup>
          )}

          {values?.reordered && (
            <Typography level={Level.TITLE_SM}>{t('labelValuesReordered')}</Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

interface IAttributeChangesProps {
  attributes?: IPartFinderProductTypeAttributeChanges;
}

export const hasProductTypeAttributeChanges = (
  attributes?: IPartFinderProductTypeAttributeChanges
) =>
  Boolean(
    attributes?.added?.length ||
      attributes?.removed?.length ||
      attributes?.renamed?.length ||
      attributes?.changed?.length ||
      attributes?.reordered
  );

// The attributes two versions differ by: added, removed, renamed, reordered and changed ones. A
// family's comparison shows its attributes the same way.
export const ProductTypeAttributeChanges = ({ attributes }: IAttributeChangesProps) => {
  const { t } = useTranslation();

  return (
    <>
      {attributes?.added?.length > 0 && (
        <CompareGroup
          title={t('labelValuesAdded', { count: attributes.added.length })}
          color={Color.SUCCESS}
        >
          {attributes.added.map((attribute) => (
            <Chip key={attribute.name} variant={Variant.OUTLINED}>
              {attribute.name}
            </Chip>
          ))}
        </CompareGroup>
      )}

      {attributes?.removed?.length > 0 && (
        <CompareGroup
          title={t('labelValuesRemoved', { count: attributes.removed.length })}
          color={Color.DANGER}
        >
          {attributes.removed.map((attribute) => (
            <Chip key={attribute.name} variant={Variant.OUTLINED}>
              {attribute.name}
            </Chip>
          ))}
        </CompareGroup>
      )}

      {attributes?.renamed?.map((renamed) => (
        <Stack
          key={`${renamed.from}-${renamed.to}`}
          direction={Direction.ROW}
          sx={{ alignItems: 'center', gap: 1 }}
        >
          <Typography
            level={Level.TITLE_SM}
            color={Color.DANGER}
            sx={{ textDecoration: 'line-through' }}
          >
            {renamed.from}
          </Typography>
          <ArrowRight />
          <Typography level={Level.TITLE_SM} color={Color.SUCCESS}>
            {renamed.to}
          </Typography>
        </Stack>
      ))}

      {attributes?.reordered && (
        <Typography level={Level.TITLE_SM}>{t('labelAttributesReordered')}</Typography>
      )}

      {attributes?.changed?.map((change) => <AttributeChange key={change.name} change={change} />)}
    </>
  );
};

interface IProps {
  fromVersionId: number;
  toVersionId: number;
  isOpen: boolean;
  onClose: () => void;
}

// What changed between two published versions of a product type, older to newer.
export const PartFinderProductTypeCompare = ({
  fromVersionId,
  toVersionId,
  isOpen,
  onClose,
}: IProps) => {
  const { t } = useTranslation();

  const {
    data: comparison,
    isLoading,
    isError,
  } = useComparePartFinderProductTypeVersions(fromVersionId, toVersionId, isOpen);

  const attributes = comparison?.attributes;
  const hasAttributeChanges = hasProductTypeAttributeChanges(attributes);

  return (
    <CompareView
      comparison={comparison}
      isLoading={isLoading}
      isError={isError}
      errorMessage={t('errorTitlePartFinderProductTypeCompare')}
      isOpen={isOpen}
      onClose={onClose}
    >
      {comparison && (
        <Stack sx={{ gap: 1 }}>
          <Typography level={Level.TITLE_MD}>{t('labelAttributes')}</Typography>

          <ProductTypeAttributeChanges attributes={attributes} />

          {!hasAttributeChanges && (
            <Typography level={Level.TITLE_SM} sx={{ color: 'neutral.600' }}>
              {t('labelNoChanges')}
            </Typography>
          )}
        </Stack>
      )}
    </CompareView>
  );
};
